if (!customElements.get('infinite-scroll')) {
  let restoredOnce = false;
  const navType = performance.getEntriesByType('navigation')[0]?.type;

  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.nextUrl = this.dataset.nextUrl;
      this.sectionId = this.dataset.sectionId;
      this.mode = this.dataset.mode || 'infinite';
      this.autoLimit = parseInt(this.dataset.autoPages || '0', 10);
      this.grid = document.querySelector(this.dataset.grid || '#product-grid');
      this.button = this.querySelector('.infinite-scroll__button');
      this.status = this.querySelector('.infinite-scroll__status');
      this.loaded = 0;
      this.loading = false;

      if (!this.grid || !this.nextUrl) return;

      const fallback = this.querySelector('.infinite-scroll__fallback');
      if (fallback) fallback.hidden = true;

      this.button.addEventListener('click', () => this.load());
      this.restore();

      if (!this.nextUrl) return this.done();
      if (this.mode === 'infinite' && !this.limitReached()) this.observe();
      else this.showButton();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    get storageKey() {
      return `infscroll:${location.pathname}${location.search}`;
    }

    limitReached() {
      return this.autoLimit > 0 && this.loaded >= this.autoLimit;
    }

    observe() {
      this.observer = new IntersectionObserver(
        (entries) => entries[0].isIntersecting && this.load(),
        { rootMargin: '0px 0px 800px 0px' }
      );
      this.observer.observe(this);
    }

    showButton() {
      this.observer?.disconnect();
      this.button.hidden = false;
    }

    done() {
      this.observer?.disconnect();
      this.button.hidden = true;
      this.classList.add('is-done');
    }

    async load() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      this.button.disabled = true;
      this.classList.add('is-loading');
      this.status.textContent = 'Loading more products';

      try {
        const url = new URL(this.nextUrl, location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);

        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const items = doc.querySelectorAll(`${this.dataset.grid || '#product-grid'} > li`);
        const frag = document.createDocumentFragment();
        items.forEach((li) => frag.appendChild(document.importNode(li, true)));
        this.grid.appendChild(frag);

        this.nextUrl = doc.querySelector('infinite-scroll')?.dataset.nextUrl || '';
        this.loaded++;
        this.status.textContent = `${items.length} more products loaded`;

        // Dawn scroll animations
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }
        // Hook for review apps, tracking, etc.
        document.dispatchEvent(new CustomEvent('infinite-scroll:loaded', { detail: { grid: this.grid } }));

        this.save();
      } catch (e) {
        this.status.textContent = "Couldn't load more products. Try again.";
        this.showButton();
      } finally {
        this.loading = false;
        this.button.disabled = false;
        this.classList.remove('is-loading');
      }

      if (!this.nextUrl) return this.done();
      if (this.mode === 'infinite' && this.limitReached()) return this.showButton();

      // If the sentinel is still in range (short pages), keep going
      if (this.mode === 'infinite' && this.button.hidden &&
          this.getBoundingClientRect().top < window.innerHeight + 800) {
        this.load();
      }
    }

    save() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          html: this.grid.innerHTML,
          nextUrl: this.nextUrl,
          loaded: this.loaded,
          scrollY: window.scrollY
        }));
      } catch (e) {}
    }

    restore() {
      if (restoredOnce || navType !== 'back_forward') return;
      restoredOnce = true;
      try {
        const state = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (!state) return;
        this.grid.innerHTML = state.html;
        this.nextUrl = state.nextUrl;
        this.loaded = state.loaded;
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }
        history.scrollRestoration = 'manual';
        requestAnimationFrame(() => window.scrollTo(0, state.scrollY));
      } catch (e) {}
    }
  }

  // Save scroll position when leaving for a PDP
  window.addEventListener('pagehide', () => {
    const el = document.querySelector('infinite-scroll');
    if (el?.loaded > 0) el.save();
  });

  customElements.define('infinite-scroll', InfiniteScroll);
}